import { wencesApi } from "@/api/wencesApi";
import { isNotaComercialModuleActive } from "./notaComercialPendingContacts";

/**
 * Notas comerciales por id, para mostrar su efecto al lado de un importe.
 *
 * Una pantalla de embarque puede tener notas colgadas de varias líneas de gasto: las que
 * se piden en la misma vuelta de eventos se resuelven en una sola llamada, con caché por
 * nota, para no sumar un request por renglón.
 */

const BATCH_DELAY_MS = 60;
const MAX_IDS_PER_REQUEST = 50;

/** Clave de caché: la misma nota vale distinto según el embarque por el que se pregunta. */
function cacheKey(noteId, shippingId) {
  return shippingId ? `${noteId}@${shippingId}` : noteId;
}

const cache = new Map();
const pending = new Map();
/** Cola por embarque: cada llamada pide un solo `?shipping=`. */
let queues = new Map();
let timer = null;

function resolveQueued(ids, shippingId, byId) {
  for (const id of ids) {
    const note = (byId && byId[id]) || null;
    const key = cacheKey(id, shippingId);
    cache.set(key, note);
    const waiters = pending.get(key) || [];
    pending.delete(key);
    for (const resolve of waiters) resolve(note);
  }
}

async function flushQueue() {
  timer = null;
  const pendingQueues = queues;
  queues = new Map();

  for (const [shippingId, idSet] of pendingQueues) {
    const ids = [...idSet];
    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);
      try {
        const params = { ids: chunk.join(",") };
        if (shippingId) params.shipping = shippingId;
        const { data } = await wencesApi.get("/api/nota-comercial/by-ids", { params });
        const byId = {};
        for (const note of (data && data.notes) || []) {
          if (note && note._id) byId[String(note._id)] = note;
        }
        resolveQueued(chunk, shippingId, byId);
      } catch (e) {
        // Es información de apoyo: si falla, el renglón no se dibuja y el importe queda como está.
        resolveQueued(chunk, shippingId, null);
      }
    }
  }
}

/**
 * @param {string} noteId
 * @returns {Promise<object|null>}
 */
export function notaComercialById(noteId, shippingId = "") {
  const id = String(noteId || "").trim();
  if (!id || !isNotaComercialModuleActive()) return Promise.resolve(null);
  const ship = String(shippingId || "").trim();
  const key = cacheKey(id, ship);
  if (cache.has(key)) return Promise.resolve(cache.get(key));

  return new Promise((resolve) => {
    const waiters = pending.get(key) || [];
    waiters.push(resolve);
    pending.set(key, waiters);
    if (!queues.has(ship)) queues.set(ship, new Set());
    queues.get(ship).add(id);
    if (!timer) timer = setTimeout(flushQueue, BATCH_DELAY_MS);
  });
}

/**
 * @param {Array} noteIds ids o referencias pobladas
 * @returns {Promise<object[]>} las que se pudieron leer
 */
export async function notasComercialesByIds(noteIds = [], shippingId = "") {
  const ids = (Array.isArray(noteIds) ? noteIds : [])
    .map((n) => (n && typeof n === "object" ? String(n._id || "") : String(n || "")))
    .filter(Boolean);
  if (!ids.length) return [];
  const notes = await Promise.all(ids.map((id) => notaComercialById(id, shippingId)));
  return notes.filter(Boolean);
}

/** Olvida lo cacheado: al crear o editar una nota el renglón tiene que volver a leerla. */
export function clearNotaComercialByIdCache(noteId) {
  const id = String(noteId || "").trim();
  if (!id) {
    cache.clear();
    return;
  }
  // La misma nota está cacheada una vez por embarque consultado.
  for (const key of [...cache.keys()]) {
    if (key === id || key.startsWith(`${id}@`)) cache.delete(key);
  }
}

/**
 * Efecto de la nota sobre el costo de la línea.
 *
 * Una nota de crédito del proveedor baja lo que le debemos, así que baja el costo; una de
 * débito lo sube. Las notas del cliente no son costo: no mueven este número.
 *
 * @param {object} note
 * @returns {number} negativo si resta al costo, positivo si suma
 */
export function expenseEffectOfNote(note) {
  if (!note) return 0;
  const role = String(note.contact_role || "").trim();
  if (!role || role.toLowerCase() === "client") return 0;
  // Lo que descuenta es lo aplicado a este embarque, no el total de la nota: de una nota
  // de 200 con 80 aplicados, al gasto solo le corresponden esos 80.
  const applied = Number(note.appliedAmount);
  const amount = Number.isFinite(applied) ? applied : Number(note.amount) || 0;
  if (!amount) return 0;
  return String(note.credit_debit || "").trim() === "Debit note" ? amount : -amount;
}

/**
 * Suma del efecto de varias notas sobre el costo.
 * @param {object[]} notes
 * @returns {number}
 */
export function expenseEffectOfNotes(notes = []) {
  return (Array.isArray(notes) ? notes : []).reduce(
    (sum, note) => sum + expenseEffectOfNote(note),
    0
  );
}
